import { MLHelper } from '@bimser/core';
import cloneDeep from 'lodash/cloneDeep';
import DataGrid2 from '..';
import { IPropertyField } from '../../../../../entities/common';
import { filterFields } from '../../../helper';
import { IDataGridColumnCollection } from '../../../../../entities';

export function getPropertyFields(
	this: DataGrid2,
	fields: IPropertyField[],
	properties?: Map<string, any>
) {
	const captionItems: IPropertyField[] = MLHelper.supportedLanguages.map(lang => ({
		name: lang.culture,
		type: 'string'
	}));

	const columnPropertiesModel: IPropertyField[] = [
		{
			name: 'align',
			type: 'string'
		},
		{
			name: 'autoIncrement',
			type: 'boolean'
		},
		{
			name: 'caption',
			type: 'object',
			items: captionItems
		},
		{
			name: 'dbType',
			type: 'object',
			items: [
				{
					name: 'columnName',
					type: 'string'
				},
				{
					name: 'type',
					type: 'string'
				}
			]
		},
		{
			name: 'formatType',
			type: 'object',
			items: [
				{
					name: 'format',
					type: 'string'
				},
				{
					name: 'type',
					type: 'string'
				}
			]
		},
		{
			name: 'editType',
			type: 'string'
		},
		{
			name: 'editingEnabled',
			type: 'boolean'
		},
		{
			name: 'filtering',
			type: 'object',
			items: [
				{
					name: 'enabled',
					type: 'boolean'
				},
				{
					name: 'operation',
					type: 'string'
				}
			]
		},
		{
			name: 'format',
			type: 'string'
		},
		{
			name: 'isPrimaryKey',
			type: 'boolean'
		},
		{
			name: 'name',
			type: 'string'
		},
		{
			name: 'precision',
			type: 'number'
		},
		{
			name: 'sorting',
			type: 'object',
			items: [
				{
					name: 'enabled',
					type: 'boolean'
				},
				{
					name: 'index',
					type: 'number'
				},
				{
					name: 'order',
					type: 'string'
				}
			]
		},
		{
			name: 'useThousandSeparator',
			type: 'boolean'
		},
		{
			name: 'visible',
			type: 'boolean'
		},
		{
			name: 'width',
			type: 'number'
		},
		{
			name: 'wordWrapEnabled',
			type: 'boolean'
		},
		{
			name: 'saveMode',
			type: 'string'
		}
	];

	const columns: IDataGridColumnCollection = properties?.get('columns') || [];

	const columnsItems: IPropertyField[] = columns
		.filter(c => c.dbType.type)
		.map(column => {
			const columnProperties = cloneDeep(columnPropertiesModel);

			const getColumnValueField = (): IPropertyField => {
				switch (column.dbType.type) {
					case 'date':
					case 'dateTime': {
						return { name: 'value', type: 'date' };
					}
					case 'boolean':
						return { name: 'value', type: 'boolean' };
					case 'string': {
						if (column.editType === 'dateRangePicker') {
							return {
								name: 'value',
								type: 'object',
								items: [
									{ name: 'startDate', type: 'date' },
									{ name: 'endDate', type: 'date' }
								]
							};
						}
						return { name: 'value', type: 'string' };
					}
					case 'time':
						return { name: 'value', type: 'time' };
					case 'decimal':
						return { name: 'value', type: 'number' };
					case 'object': {
						if (column.editType == 'multiLanguageTextBox') {
							return {
								name: 'culture',
								type: 'object',
								items: captionItems
							};
						} else if (column.editType == 'comboBox') {
							return {
								name: 'data',
								type: 'object',
								items:
									properties
										.get('columns')
										.find(gridColumn => gridColumn.name === column.name)
										?.dataFields?.map(i => ({ name: i.name, type: i.type })) ||
									[],
								readOnly: true
							};
						} else if (column.editType === 'dateRangePicker') {
							return {
								name: 'value',
								type: 'object',
								items: [
									{ name: 'startDate', type: 'date' },
									{ name: 'endDate', type: 'date' }
								]
							};
						}
					}
				}
			};

			columnProperties.push(getColumnValueField());

			return {
				name: column.name,
				type: 'object',
				items: columnProperties.filter(properties => properties)
			};
		});

	return [
		...filterFields(fields, ['readOnly']),
		{
			name: 'selectedRowKeys',
			type: 'object',
			readOnly: true,
			items: [
				{
					name: 'length',
					type: 'number'
				}
			]
		},
		{
			name: 'selectedRowsData',
			type: 'object',
			readOnly: true,
			items: [
				{
					name: 'length',
					type: 'number'
				}
			]
		},
		{
			name: 'columns',
			type: 'object',
			items: columnsItems
		},
		{
			name: 'rows',
			type: 'object',
			items: [
				...columnsItems.map<IPropertyField>(({ name, type }) => ({
					name,
					type,
					hiddenOn: ['formula-manager', 'rule-manager']
				})),
				{
					name: 'length',
					type: 'number',
					hiddenOn: ['data-synchronizer'],
					readOnly: true
				}
			],
			readOnly: true,
			isArray: true
		},
		{
			name: 'displayType',
			type: 'string'
		},
		{
			name: 'wordWrapEnabled',
			type: 'boolean'
		},
		{
			name: 'columnHidingEnabled',
			type: 'boolean'
		},
		{
			name: 'cacheSettingsEnabled',
			type: 'boolean'
		},
		{
			name: 'columnFixingEnabled',
			type: 'boolean'
		},
		{
			name: 'rowDragging',
			type: 'object',
			items: [
				{
					name: 'enable',
					type: 'boolean'
				},
				{
					name: 'showDragIcons',
					type: 'boolean'
				}
			]
		}
	];
}
